/** ArcGIS Online portal item IDs previously auto-seeded for TN Guard demos. */
export const TENNESSEE_NATIONAL_GUARD_LAYER_IDS = [
  '0aca4701ef8a482abb9efe6ecbf2f131',
  '54381d8b02fc4252b1460bb3eccd22c9',
] as const;

export type TennesseeNationalGuardLayerId =
  (typeof TENNESSEE_NATIONAL_GUARD_LAYER_IDS)[number];

/** Ting Outages TN Floods 4_2025 AOI View — time-enabled layer. */
export const TING_OUTAGES_TN_FLOODS_ITEM_ID =
  '0aca4701ef8a482abb9efe6ecbf2f131' as const satisfies TennesseeNationalGuardLayerId;

/** FloodingBins Demo — capture_date time filter. */
export const FLOODING_BINS_DEMO_ITEM_ID =
  '54381d8b02fc4252b1460bb3eccd22c9' as const satisfies TennesseeNationalGuardLayerId;

/**
 * TN Flood 4_2025 OSINT View — no longer seeded; kept for cleanup of leftover
 * rows.
 */
export const TN_FLOOD_OSINT_ITEM_ID = 'cfe3b4a7f32848e5a2617d6e6bb5cea3';

/**
 * Previously auto-seeded Ting / FloodingBins / retired OSINT item IDs. Used to
 * delete leftover hub rows and skip PortalItem map sync.
 */
export const TN_DEMO_FORCE_DEFAULT_OFF_ITEM_IDS = [
  ...TENNESSEE_NATIONAL_GUARD_LAYER_IDS,
  TN_FLOOD_OSINT_ITEM_ID,
] as const;

export const TN_DEMO_FORCE_DEFAULT_OFF_ITEM_ID_SET = new Set<string>(
  TN_DEMO_FORCE_DEFAULT_OFF_ITEM_IDS
);

export function isTingOutagesTnFloodsLayer(layer: {
  arcgisItemId?: string | null;
  name?: string | null;
}): boolean {
  if (layer.arcgisItemId === TING_OUTAGES_TN_FLOODS_ITEM_ID) {
    return true;
  }
  // Name fallback only when item id is missing (edited/legacy rows).
  if (!layer.arcgisItemId && layer.name) {
    return layer.name.includes('Ting Outages TN Floods');
  }
  return false;
}

/** FloodingBins Demo — filter AI/map features on capture_date. */
export function isFloodingBinsDemoLayer(layer: {
  arcgisItemId?: string | null;
  name?: string | null;
  serviceUrl?: string | null;
}): boolean {
  if (layer.arcgisItemId === FLOODING_BINS_DEMO_ITEM_ID) {
    return true;
  }
  if (layer.name?.includes('FloodingBins Demo')) {
    return true;
  }
  if (layer.serviceUrl?.includes('FloodingBins')) {
    return true;
  }
  return false;
}

export type ExternalLayerTimeConfig = {
  enabled: true;
  preferredDateField?: string;
};

/**
 * Time-slider config for external layers that support datetime filtering in the
 * Layers tab and AI data-source pickers.
 */
export function getExternalLayerTimeConfig(layer: {
  arcgisItemId?: string | null;
  name?: string | null;
  serviceUrl?: string | null;
}): ExternalLayerTimeConfig | null {
  if (isTingOutagesTnFloodsLayer(layer)) {
    return { enabled: true, preferredDateField: 'displaydate' };
  }
  if (isFloodingBinsDemoLayer(layer)) {
    return { enabled: true, preferredDateField: 'capture_date' };
  }
  return null;
}

/** Attribute field stamped onto selectedLayers for COMS datetime WHERE clauses. */
export function resolveTimeFilterField(layer: {
  arcgisItemId?: string | null;
  name?: string | null;
  serviceUrl?: string | null;
}): string | undefined {
  const config = getExternalLayerTimeConfig(layer);
  if (config?.preferredDateField) return config.preferredDateField;
  if (isFloodingBinsDemoLayer(layer)) return 'capture_date';
  if (isTingOutagesTnFloodsLayer(layer)) return 'displaydate';
  return undefined;
}
